import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    DateRange,
    DynamicDateStatusElement,
    DynamicDateStatusElementInput,
    ElementsFactory,
} from 'raain-ui';

@Component({
    selector: 'raain-date-dynamic',
    templateUrl: './raain-date-dynamic.component.html',
    styleUrls: ['./raain-date-dynamic.component.scss'],
})
export class RaainDateDynamicComponent implements AfterViewChecked, OnDestroy, OnChanges {
    @Input()
    public points: {
        label: string;
        style: string;
        values: {date: Date; value: number}[];
    }[];

    @Input()
    public focusDate: Date;
    @Input()
    public focusRange: DateRange;
    @Input()
    public withoutAll: boolean;
    @Input()
    public currentHeight: number;
    public currentWidth: number;
    @Input()
    public fetchData: (
        focusDate: Date,
        focusRange: DateRange
    ) => Promise<{label: string; style: string; values: {date: Date; value: number}[]}[]>;

    @Output()
    public changedDate = new EventEmitter<Date>();

    @ViewChild('canvasElement')
    protected canvasElement: ElementRef;
    private dynamicDateStatusElement: DynamicDateStatusElement;
    private onSizeChanged;
    private created: boolean;

    constructor(private ngZone: NgZone) {
        this.focusDate = new Date();
        this.focusRange = DateRange.CENTURY;
        this.onSizeChanged = () => {};
    }

    public ngAfterViewChecked(): void {
        this.create();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.change(changes);
    }

    public ngOnDestroy(): void {
        this.delete();
    }

    focusPrevious() {
        this.dynamicDateStatusElement.focusPrevious();
    }

    focusReset() {
        this.dynamicDateStatusElement.focusReset();
    }

    focusNext() {
        this.dynamicDateStatusElement.focusNext();
    }

    @HostListener('window:resize', ['$event'])
    private onResize(event: any): void {
        this.updateMapSize();
    }

    // Map style to a valid Chart.js chart type
    private mapStyleToChartType(style: string): string {
        // List of valid Chart.js chart types
        const validChartTypes = [
            'line',
            'bar',
            'radar',
            'doughnut',
            'pie',
            'polarArea',
            'bubble',
            'scatter',
        ];

        // If style is a valid chart type, return it, otherwise default to 'line'
        return validChartTypes.includes(style) ? style : 'line';
    }

    private async create() {
        if (this.created) {
            return;
        }
        this.created = true;

        const monthSampleData = await this.fetchData(new Date(), DateRange.MONTH);

        const factory = new ElementsFactory();
        try {
            this.dynamicDateStatusElement = factory.createDynamicDateStatus(
                this.canvasElement.nativeElement,
                new DynamicDateStatusElementInput(
                    async (focusDate, focusRange) => {
                        const data = await this.fetchData(focusDate, focusRange);
                        return data.map((item) => ({
                            ...item,
                            style: this.mapStyleToChartType(item.style),
                        }));
                    },
                    {
                        dataLength: monthSampleData.length,
                        chartMinValue: -10,
                        chartMaxValue: 100,
                        onLastClick: (date: Date) => {
                            this.changedDate.emit(date);
                        },
                    }
                )
            );
        } catch (e) {
            console.warn('Dynamic focus creation issue', e);
        }
    }

    private async delete() {
        this.dynamicDateStatusElement?.chart?.destroy();
        this.created = false;
    }

    private async change(changes: SimpleChanges) {
        await this.delete();
        await this.create();
    }

    private updateMapSize(): void {
        const mapDivWidth = this.canvasElement.nativeElement.parentNode?.parentNode['clientWidth'];
        const padding = 20;
        let currentWidth =
            mapDivWidth ||
            window.innerWidth ||
            document.documentElement.clientWidth ||
            document.body.clientWidth;
        const currentHeight =
            this.currentHeight ||
            window.innerHeight ||
            document.documentElement.clientHeight ||
            document.body.clientHeight;
        currentWidth -= padding;
        this.currentWidth = currentWidth;
        this.currentHeight = currentHeight;

        this.ngZone.run(() => {
            this.onSizeChanged();
        });
    }
}
